/**
 * Visão 360 do cliente — **a agregação REAL** (Task 13, brief 3.4 / D-13.4).
 *
 * Esta é a tela que a regra transversal da fase ("mesmos registros, mesmo
 * histórico") existe para proteger, então leia os invariantes antes de mexer:
 *
 * 1. **Um cliente só.** O cadastro é o cliente CANÔNICO (`customers` +
 *    `customer_identities`, migration 0020). O id que chega na rota pode ser o
 *    id canônico OU o `customerId` que a conversa guarda (que no WhatsApp é o
 *    telefone do inbound); os dois caminhos são resolvidos para o MESMO
 *    cliente (`resolveCrmCustomer`). Não existe "cliente da conversa" e
 *    "cliente do pedido" nesta tela.
 * 2. **Os números vêm dos registros, e os registros vêm com eles.** Cada
 *    total devolve também a lista que o compõe (pedidos, conversas,
 *    orçamentos, oportunidades, tarefas) para a tela navegar até a origem.
 *    Nenhum número é somado de um resumo gravado.
 * 3. **Ausência de fonte não vira zero.** O contrato `Customer360` de
 *    `packages/ui` exige números; quando a fonte de pedidos unificados não está
 *    publicada, o histórico de compras cai no pedido do checkout da Loja e
 *    **declara** (`purchaseSource: "checkout_fallback"`) que outros canais
 *    ficam fora — em vez de apresentar a Loja como a vida inteira do cliente.
 * 4. **Venda desfeita não conta.** O total gasto soma só pedidos em estado de
 *    receita (`ORDER_REVENUE_STATES`): cancelado e devolvido ficam visíveis na
 *    lista, mas fora do total.
 * 5. **Puro e sem I/O**: recebe datasets e devolve estrutura (testável sem
 *    navegador). Endereço provisório registrado no ADR-0007.
 */
import {
  assertCustomer360,
  type AuditLogEntry,
  type Customer360,
  type LeadStage
} from "../../../../packages/ui/src/types.js";
import { leadStageFromScore, normalizePhone, ordersOfCustomer, toAuditLogEntry } from "../dashboard/customer.js";
import { isRevenueState } from "../orders/state-machine.js";
import { isClosedStage, effectiveQuoteStatus, type OpportunitySource } from "./pipeline.js";
import {
  companyOfCustomer,
  crmDatasetFromPayload,
  emptyCrmDataset,
  isCrmSourceAwaitingContract,
  opportunitiesOfCustomer,
  quoteView,
  quotesOfCustomer,
  resolveCrmCustomer,
  tasksOfCustomer,
  type CrmDataset,
  type QuoteView
} from "./dataset.js";
import { toCrm360Order, type Crm360Order, type Crm360Payload } from "./contract.js";
import { evaluateSegments, matchedSegmentKeys, type CustomerSegmentFacts, type SegmentEvaluation } from "./segments.js";
import { applyLgpdMask, crm360ExportSummary, isAnonymizedCustomer, type Crm360ExportSummary } from "./lgpd.js";
import { emptyOrdersDataset, isOrdersSourceAwaitingContract, type OrdersDataset } from "../orders/dataset.js";
import { taskDueState, type TaskDueState } from "./pipeline.js";
import type { DashboardDataset } from "../dashboard/kpis.js";
import type {
  CrmCompany,
  CrmCustomer,
  CrmOpportunity,
  CrmTask,
  PlatformAuditEvent,
  PlatformAuditEvent as AuditEvent,
  PlatformConversation,
  PlatformOrder
} from "../api/types.js";

/** De onde vem o histórico de compras exibido. */
export type PurchaseSource = "unified" | "checkout_fallback";

/** Como o vínculo cliente↔pedido foi estabelecido (sempre declarado na tela). */
export type CustomerOrderLink = "canonical" | "phone" | "unresolved";

export interface Crm360TaskRow {
  task: CrmTask;
  due: TaskDueState;
}

export interface Crm360View {
  /** Contrato `Customer360` de `packages/ui`, já validado. */
  customer: Customer360;
  /** Cadastro canônico (0020); `null` = o id não resolveu para um cadastro. */
  canonical: CrmCustomer | null;
  /** Empresa B2B (0018) e seus contatos; `null` em pessoa física. */
  company: CrmCompany | null;
  conversations: PlatformConversation[];
  /** Histórico de compras unificado (todos os canais), quando disponível. */
  orders: Crm360Order[];
  /**
   * De onde veio o recorte do cliente: o runtime persistente (`/api/crm/360`,
   * cliente resolvido por `resolve_customer_by_identity` no servidor) ou as
   * listas do CRM filtradas na tela. A tela DECLARA isso — as duas respostas
   * precisam ser o mesmo cliente, e dizer qual caminho respondeu é o que torna
   * uma divergência visível em vez de silenciosa.
   */
  aggregationSource: "crm_360_runtime" | "crm_lists";
  /** Pedidos do checkout usados como fallback declarado. */
  checkoutOrders: PlatformOrder[];
  purchaseSource: PurchaseSource;
  /** Pedidos que contam no total gasto (estado de receita). */
  revenueOrderIds: string[];
  opportunities: CrmOpportunity[];
  /** Negociação em aberto mais recente — é o estágio comercial exibido. */
  activeOpportunity: CrmOpportunity | null;
  quotes: QuoteView[];
  tasks: Crm360TaskRow[];
  segments: SegmentEvaluation[];
  facts: CustomerSegmentFacts;
  orderLink: CustomerOrderLink;
  /** `true` enquanto o estágio vem de faixa de score e não de uma oportunidade. */
  leadStageEstimated: boolean;
  /** `false` = a fonte de auditoria (`/api/admin/audit`) não está publicada. */
  auditAvailable: boolean;
  /**
   * Portabilidade LGPD do relacionamento (`packages/admin-ops`): o que o
   * pacote do titular carrega. `null` sem cadastro canônico resolvido — não há
   * titular a exportar.
   */
  personalData: Crm360ExportSummary | null;
  /** `true` = cadastro anonimizado; o contato exibido já vem mascarado. */
  anonymized: boolean;
  /** Estado por bloco do CRM, para a tela declarar o que não respondeu. */
  crmSources: CrmDataset["sources"];
  ordersSourceAwaiting: boolean;
}

export interface Crm360Input {
  dataset: DashboardDataset;
  customerId: string;
  auditEvents: PlatformAuditEvent[] | null;
  /** Pedidos unificados (Task 12). Ausente = fonte não carregada. */
  orders?: OrdersDataset;
  /** CRM (Task 13). Ausente = fonte não carregada. */
  crm?: CrmDataset;
  /**
   * Payload de `GET /api/crm/360` — o caminho CANÔNICO. Quando presente, ele
   * é a autoridade sobre QUAIS registros são deste cliente (o servidor já
   * resolveu a identidade pela 0020 e filtrou as cinco fontes), e os datasets
   * de lista deixam de ser consultados para esse recorte. Ausente = runtime do
   * CRM não publicado neste ambiente, e a tela cai nas listas declarando isso.
   */
  crm360?: Crm360Payload | null;
  now?: Date;
}

/** Oportunidade exibida como estágio atual: a aberta mais recente. */
export function activeOpportunityOf(opportunities: CrmOpportunity[]): CrmOpportunity | null {
  return opportunities.find((opportunity) => !isClosedStage(opportunity.stage)) ?? null;
}

/**
 * Pedidos unificados do cliente canônico.
 *
 * O vínculo preferido é o `customerId` do pedido (resolvido no servidor pela
 * identidade do canal, D-12.15/16) — por isso `link: "canonical"`. Sem
 * cadastro resolvido, caímos no telefone, que é o que a Task 10 já fazia, e a
 * tela diz qual caminho foi usado.
 */
export function unifiedOrdersOfCustomer(
  orders: OrdersDataset,
  canonical: CrmCustomer | null,
  phones: string[]
): { orders: Crm360Order[]; link: CustomerOrderLink } {
  if (canonical) {
    const byCustomer = orders.orders.filter((order) => order.customerId === canonical.id);
    return { orders: byCustomer.map(toCrm360Order), link: "canonical" };
  }
  const wanted = new Set(phones.map(normalizePhone).filter(Boolean));
  if (!wanted.size) return { orders: [], link: "unresolved" };
  return {
    orders: orders.orders
      .filter((order) => wanted.has(normalizePhone(order.customer?.phone)))
      .map(toCrm360Order),
    link: "phone"
  };
}

/** Canais em que o cliente aparece (origem de pedidos + de oportunidades). */
function channelsOf(orders: Crm360Order[], opportunities: CrmOpportunity[]): OpportunitySource[] {
  const channels = new Set<OpportunitySource>();
  for (const order of orders) channels.add(order.origin);
  for (const opportunity of opportunities) channels.add(opportunity.source);
  return [...channels];
}

/**
 * Visão 360 do cliente sobre dados reais. `null` quando o id não aparece em
 * nenhum registro conhecido (conversa, cadastro canônico, pedido ou CRM) — a
 * Plataforma não inventa cadastro para preencher a tela.
 */
export function buildCrm360(input: Crm360Input): Crm360View | null {
  const { dataset, customerId, auditEvents } = input;
  const now = input.now ?? new Date();
  const orders = input.orders ?? emptyOrdersDataset();
  // Payload canônico do runtime quando ele respondeu; as listas continuam
  // servindo o ambiente em que a rota não está publicada.
  const payload = input.crm360 ?? null;
  const crm = payload ? crmDatasetFromPayload(payload) : (input.crm ?? emptyCrmDataset());

  // --- cadastro unificado: um cliente, resolvido pelos dois caminhos -------
  const conversationsById = dataset.conversations.filter((conversation) => conversation.customerId === customerId);
  const identityPhone = conversationsById.map((conversation) => conversation.phone).find(Boolean) ?? null;
  // Com o payload, a resolução de identidade já foi feita pelo BANCO
  // (`resolve_customer_by_identity`, 0020). Sem ele, a tela usa o espelho de
  // leitura sobre o cadastro que a rota de lista publicou.
  const resolved = payload ? payload.customer : resolveCrmCustomer(crm, { customerId, phone: identityPhone });
  // Direito ao esquecimento: a resolução continua acontecendo sobre o cadastro
  // como ele está, mas o que esta tela EXIBE do titular anonimizado passa pela
  // máscara compartilhada de `packages/admin-ops` — o 360 agrega seis fontes e
  // não pode ser a tela que devolve o contato apagado.
  const canonical = resolved ? applyLgpdMask(resolved) : null;
  // Quando o id da rota é o canônico, as conversas do cliente podem estar
  // guardadas sob o telefone do canal: as duas pontas entram na mesma lista.
  //
  // Com o payload, QUAIS conversas são deste cliente é decisão do runtime (ele
  // casou o id canônico com as identidades verificadas da 0020); o dataset do
  // atendimento continua fornecendo os campos exibidos (canal, modo, nome),
  // que a visão 360 não reimplementa.
  const canonicalConversationIds = payload ? new Set(payload.conversations.map((item) => item.id)) : null;
  const conversations = (canonicalConversationIds
    ? dataset.conversations.filter((conversation) => canonicalConversationIds.has(conversation.id))
    : canonical
      ? dataset.conversations.filter((conversation) => {
          if (conversation.customerId === customerId) return true;
          const resolved = resolveCrmCustomer(crm, {
            customerId: conversation.customerId,
            phone: conversation.phone
          });
          return resolved?.id === canonical.id;
        })
      : conversationsById
  ).sort((a, b) => a.updatedAt.localeCompare(b.updatedAt));

  const phones = [
    ...conversations.map((conversation) => conversation.phone ?? "").filter(Boolean),
    canonical?.phone ?? ""
  ].filter(Boolean);

  // --- histórico de compras ------------------------------------------------
  // O payload traz os pedidos do MESMO cliente canônico já filtrados por
  // `platform_orders.customer_id` no servidor: nesse caminho a fonte unificada
  // respondeu por definição, e o vínculo é o do cadastro.
  const ordersAwaiting = payload ? false : isOrdersSourceAwaitingContract(orders);
  const unified = payload
    ? { orders: payload.orders, link: "canonical" as CustomerOrderLink }
    : unifiedOrdersOfCustomer(orders, canonical, phones);
  // Fonte unificada ausente: usamos o pedido do checkout da Loja e DIZEMOS que
  // é recorte de um canal só (os números abaixo passam a ser desse recorte).
  const checkoutOrders = ordersAwaiting ? ordersOfCustomer(dataset, phones) : [];
  const purchaseSource: PurchaseSource = ordersAwaiting ? "checkout_fallback" : "unified";
  const revenueUnified = unified.orders.filter((order) => isRevenueState(order.state));
  const revenueCheckout = checkoutOrders.filter((order) => order.status === "paid");
  const totalOrders = purchaseSource === "unified" ? unified.orders.length : checkoutOrders.length;
  const totalSpentCents =
    purchaseSource === "unified"
      ? revenueUnified.reduce((sum, order) => sum + (order.totalCents ?? 0), 0)
      : revenueCheckout.reduce((sum, order) => sum + (order.totalCents ?? 0), 0);
  const lastPurchaseAt =
    purchaseSource === "unified"
      ? (revenueUnified.map((order) => order.placedAt).sort().at(-1) ?? null)
      : (revenueCheckout.map((order) => order.placedAt).sort().at(-1) ?? null);
  const orderLink: CustomerOrderLink = ordersAwaiting
    ? phones.length
      ? "phone"
      : "unresolved"
    : unified.link;

  // --- CRM: pipeline, propostas, tarefas -----------------------------------
  const crmCustomerId = canonical?.id ?? customerId;
  const opportunities = opportunitiesOfCustomer(crm, crmCustomerId);
  const activeOpportunity = activeOpportunityOf(opportunities);
  const quotes = quotesOfCustomer(crm, crmCustomerId).map((quote) => quoteView(quote, now));
  const tasks = tasksOfCustomer(crm, crmCustomerId).map((task) => ({ task, due: taskDueState(task, now) }));

  // Cliente desconhecido em TODAS as fontes: não há 360 a montar.
  if (
    !conversations.length &&
    !canonical &&
    !unified.orders.length &&
    !checkoutOrders.length &&
    !opportunities.length &&
    !quotes.length
  ) {
    return null;
  }

  const latestConversation = conversations.at(-1) ?? null;
  const leadScore = latestConversation?.leadScore ?? 0;
  // Estágio comercial REAL quando existe oportunidade aberta; só então o
  // estágio deixa de ser estimativa por faixa de score (D-13.9).
  const leadStage: LeadStage = activeOpportunity?.stage ?? opportunities[0]?.stage ?? leadStageFromScore(leadScore);
  const leadStageEstimated = opportunities.length === 0;

  // --- segmentação (regra avaliada, nunca lista materializada) -------------
  const facts: CustomerSegmentFacts = {
    paidOrderCount: purchaseSource === "unified" ? revenueUnified.length : revenueCheckout.length,
    totalSpentCents,
    lastPurchaseAt,
    // "Disponível" significa: alguma fonte de pedido respondeu. No fallback a
    // resposta existe, mas é de um canal só — e a tela declara isso junto do
    // segmento, para ninguém ler "cliente inativo" quando o que falta é fonte.
    ordersAvailable: !ordersAwaiting || checkoutOrders.length > 0,
    lastInteractionAt: latestConversation?.updatedAt ?? null,
    stages: opportunities.map((opportunity) => opportunity.stage),
    hasOpenQuote: quotes.some((item) => item.status === "draft" || item.status === "sent"),
    channels: channelsOf(unified.orders, opportunities),
    companyId: canonical?.companyId ?? null
  };
  const segments = evaluateSegments(crm.segments, facts, now);

  // --- auditoria -----------------------------------------------------------
  const aggregateIds = new Set<string>([
    customerId,
    crmCustomerId,
    ...conversations.map((conversation) => conversation.id),
    ...unified.orders.map((order) => order.id),
    ...unified.orders.map((order) => order.checkoutOrderId ?? ""),
    ...checkoutOrders.map((order) => order.id),
    ...opportunities.map((opportunity) => opportunity.id)
  ]);
  aggregateIds.delete("");
  const auditLog: AuditLogEntry[] = (auditEvents ?? [])
    .filter((event: AuditEvent) => aggregateIds.has(event.aggregateId))
    .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt))
    .map(toAuditLogEntry);

  // Titular anonimizado: o contato NÃO é recuperado das outras fontes. O
  // snapshot de contato do pedido e o telefone do canal continuam no registro
  // de origem (não podemos reescrever a venda), mas a tela do cliente não os
  // reapresenta como o contato dele — isso desfaria a anonimização.
  const anonymized = canonical !== null && isAnonymizedCustomer(canonical);
  const customer: Customer360 = {
    customerId: crmCustomerId,
    name: canonical?.name || latestConversation?.displayName || customerId,
    phone: anonymized ? "" : (canonical?.phone ?? phones.at(-1) ?? ""),
    email: anonymized
      ? undefined
      : (canonical?.email ??
        unified.orders.find((order) => order.customer?.email)?.customer?.email ??
        checkoutOrders.find((order) => order.customer?.email)?.customer?.email),
    leadScore,
    leadStage,
    totalOrders,
    totalSpentCents,
    lastInteractionAt: latestConversation?.updatedAt ?? lastPurchaseAt ?? canonical?.createdAt ?? now.toISOString(),
    // Tags = segmentos a que o cliente pertence pela regra salva. Vazio aqui
    // significa "nenhum segmento casou" (ou fonte de segmentos ausente, o que
    // a tela declara separadamente) — nunca um rótulo inventado.
    tags: matchedSegmentKeys(segments),
    conversationIds: conversations.map((conversation) => conversation.id),
    auditLog
  };
  assertCustomer360(customer);

  return {
    customer,
    canonical,
    company: companyOfCustomer(crm, canonical),
    conversations,
    orders: unified.orders,
    checkoutOrders,
    purchaseSource,
    revenueOrderIds: (purchaseSource === "unified" ? revenueUnified : revenueCheckout).map((order) => order.id),
    opportunities,
    activeOpportunity,
    quotes,
    tasks,
    segments,
    facts,
    orderLink,
    aggregationSource: payload ? "crm_360_runtime" : "crm_lists",
    leadStageEstimated,
    auditAvailable: auditEvents !== null,
    // O pacote de portabilidade é montado sobre os MESMOS registros agregados
    // acima (um export que discordasse do 360 descreveria outro cliente).
    personalData: canonical
      ? crm360ExportSummary({
          customer: canonical,
          conversations,
          orders: purchaseSource === "unified" ? unified.orders : checkoutOrders,
          opportunities,
          quotes: quotes.map((item) => item.quote)
        })
      : null,
    anonymized,
    crmSources: crm.sources,
    ordersSourceAwaiting: ordersAwaiting
  };
}

/** `true` quando a fonte de segmentos não respondeu (tags não avaliáveis). */
export function areSegmentsAwaiting(view: Crm360View): boolean {
  return view.crmSources.segments === "awaiting_contract";
}

/** Atalho usado pela tela: o CRM respondeu alguma coisa para este cliente? */
export function isCrmBlockAwaiting(view: Crm360View, source: keyof CrmDataset["sources"]): boolean {
  return isCrmSourceAwaitingContract({ ...emptyCrmDataset(), sources: view.crmSources }, source);
}

/** Situação efetiva da proposta, reexportada para a tela não reimplementar. */
export { effectiveQuoteStatus };
